import { animate, style, transition, trigger } from '@angular/animations';
import { ChangeDetectorRef, Component, ElementRef, Input, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ICONS } from '@constants';
import { TranslateService } from '@ngx-translate/core';
import { FitBoundsOptions, LatLngBounds, MapOptions } from 'leaflet';

import mapboxgl, { Map, Marker } from 'mapbox-gl';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import { EnvService, ReportsService } from '@services';

@Component({
  selector: 'app-location',
  templateUrl: './location.component.html',
  styleUrls: ['./location.component.scss'],
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-20px)' }),
        animate('0.3s ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
    ]),
  ],
})
export class LocationComponent {
  @Input() parentForm: FormGroup;

  public isFetchingCurrentLocation = false;
  public isLocationBlocked = false;
  public showMapContainer = false;
  public mapReady = false;

  public icons = ICONS;
  public Object = Object;
  @ViewChild('mapContainer') mapContainer!: ElementRef<HTMLDivElement>;

  public congressionalDistrictOptions: {
    id: string;
    text: string;
    value: string;
    ocdId: string | undefined;
  }[];

  // Map properties
  private map: Map;
  public mapMarker: Marker;
  public geocoder: MapboxGeocoder;
  public location: { lat: number; lng: number } = {
    lat: 41.1,
    lng: -106.77,
  };
  public mapLayers: any[] = [];
  public leafletOptions: MapOptions;
  public geocoderControl: any;

  public mapFitToBounds: LatLngBounds;
  public fitBoundsOptions: FitBoundsOptions = {
    animate: true,
  };

  public locationButtons: { [key: string]: { text: string; active: boolean } } = {
    current: {
      text: 'form.location.use_current_location',
      active: false,
    },
    map: {
      text: 'form.location.search_or_place_pin',
      active: false,
    },
  };

  constructor(
    private snackbar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    private env: EnvService,
    private reportsService: ReportsService,
  ) {
    this.congressionalDistrictOptions = [];
  }

  ngOnInit(): void {
    mapboxgl.accessToken = this.env.environment.mapbox_api_key;
    this.fetchCongressionalDistricts();
  }

  ngAfterViewInit() {
    if (this.showMapContainer) {
      this.initMap(this.mapContainer.nativeElement);
    }
  }

  private fetchCongressionalDistricts() {
    if (this.congressionalDistrictOptions.length === 0) {
      this.reportsService.getCongressionalDistricts().subscribe({
        next: (districts) => {
          if (districts.data) {
            this.congressionalDistrictOptions = districts.data.map((district) => ({
              id: district.id,
              text: district.display_name,
              value: district.display_name,
              ocdId: district.ocd_id,
            }));
          }
        },
        error: (err) => {
          console.error('Error fetching congressional districts:', err);
        },
      });
    }
  }

  public initMap(container: HTMLElement) {
    this.map = new mapboxgl.Map({
      container,
      style: 'mapbox://styles/mapbox/outdoors-v12',
      center: [this.location.lng, this.location.lat],
      zoom: 17,
    });

    this.map.addControl(new mapboxgl.NavigationControl(), 'top-left');

    const geolocate = new mapboxgl.GeolocateControl({
      positionOptions: {
        enableHighAccuracy: true,
      },
      trackUserLocation: false,
      showAccuracyCircle: true,
      showUserHeading: true,
    });

    this.map.addControl(geolocate, 'top-left');

    this.map.addControl(new mapboxgl.FullscreenControl(), 'top-left');

    this.geocoder = new MapboxGeocoder({
      accessToken: this.env.environment.mapbox_api_key,
      mapboxgl: mapboxgl as any,
      marker: false,
      placeholder: this.translate.instant('Search address...'),
    });

    this.map.addControl(this.geocoder, 'bottom-left');

    this.geocoder.on('result', (e) => {
      const [lng, lat] = e.result.center;
      this.location = { lat, lng };
      this.updateLocationControl();
      this.addMarker();
      this.map.flyTo({ center: [lng, lat], zoom: 17 });
    });

    this.map.on('click', (e) => {
      this.location = {
        lat: e.lngLat.lat,
        lng: e.lngLat.lng,
      };
      this.updateLocationControl();
      this.addMarker();
      this.cdr.detectChanges();
    });

    this.addMarker();
  }

  private async getOcdId(data: { lng: number; lat: number }): Promise<string | null> {
    try {
      const place = await fetch(
        `${this.env.environment.mapbox_geocoding_url}/${data.lng},${data.lat}.json?access_token=${this.env.environment.mapbox_api_key}`,
      );
      const placeName = (await place.json()).features[0].place_name;

      const divisions = await fetch(
        `${this.env.environment.civic_api_v2_url}/divisionsByAddress?address=${encodeURIComponent(
          placeName,
        )}&key=${this.env.environment.civic_api_key}`,
      );
      const divisionData = (await divisions.json()).divisions;

      let ocdId = Object.keys(divisionData).filter((id) => id.includes('cd:'))[0];
      const districtNumber = ocdId.split('cd:')[1];

      if (districtNumber !== 'at-large' && parseInt(districtNumber, 10) < 10) {
        ocdId = `${ocdId.split('cd:')[0]}cd:0${parseInt(districtNumber, 10)}`;
      }

      const result = this.congressionalDistrictOptions.filter((item) => item.ocdId === ocdId)[0]
        .value;

      return result ?? null;
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  private async updateCongressionalDistrict(data: { lng: number; lat: number }) {
    const ocdId = await this.getOcdId(data);

    const control = this.parentForm.get('location_group.congressional_district');

    if (!control) return;

    control.setValue(ocdId);

    if (ocdId == null) {
      control.setErrors({ noDistrict: true });
    } else {
      control.setErrors(null);
    }

    control.markAsDirty();
    control.markAsTouched();
  }

  private addMarker() {
    if (this.mapMarker) {
      this.mapMarker.remove();
    }

    this.mapMarker = new mapboxgl.Marker({ draggable: true })
      .setLngLat([this.location.lng, this.location.lat])
      .addTo(this.map);

    this.mapMarker.on('dragend', () => {
      const { lat, lng } = this.mapMarker.getLngLat();
      this.location = { lat, lng };
      this.updateLocationControl();
      this.cdr.detectChanges();
    });

    this.updateCongressionalDistrict({ lng: this.location.lng, lat: this.location.lat });
  }

  private updateLocationControl() {
    const locationControl = this.parentForm.get('location_group.location');
    locationControl?.setValue({
      coordinates: [this.location.lng, this.location.lat], // GeoJSON format: [longitude, latitude]
      type: 'Point',
    });
    locationControl?.markAsDirty();
  }

  private showMessage(message: string, type: string) {
    this.snackbar.open(message, this.translate.instant('form.close'), {
      panelClass: [type],
      duration: 6000,
    });
  }

  public async handleLocationButtonClick(event: Event, value: string) {
    // Removing line below will cause button to trigger submit
    event.preventDefault();

    Object.keys(this.locationButtons).forEach((key) => {
      if (key === value) {
        this.locationButtons[key].active = true;
      } else {
        this.locationButtons[key].active = false;
      }
    });

    switch (value) {
      case 'current':
        this.setLocationValue();
        break;
      case 'map':
        this.showMap();
        break;
    }
  }

  public showMap() {
    this.showMapContainer = true;
    this.cdr.detectChanges();

    setTimeout(() => {
      if (!this.map) {
        this.initMap(this.mapContainer.nativeElement);
      }
    });
  }

  public getLocationSuccess(position: GeolocationPosition) {
    this.isFetchingCurrentLocation = false;

    this.location = {
      lat: position.coords.latitude,
      lng: position.coords.longitude,
    };

    const locationControl = this.parentForm.get('location_group.location');
    locationControl?.setValue({
      coordinates: [position.coords.longitude, position.coords.latitude],
      type: 'Point',
    });
    locationControl?.markAsDirty();

    this.updateCongressionalDistrict(this.location);

    this.showMap();

    if (this.showMapContainer && this.map) {
      this.map.flyTo({
        center: [this.location.lng, this.location.lat],
        zoom: 15,
      });
      this.addMarker();
    }
  }

  public getLocationError() {
    this.showMessage('Unable to retrieve current location.', 'error');
    this.isFetchingCurrentLocation = false;
    this.isLocationBlocked = true;
  }

  public setLocationValue() {
    this.isFetchingCurrentLocation = true;

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => this.getLocationSuccess(position),
        () => {
          this.showMessage('Unable to retrieve current location.', 'error');
          this.isFetchingCurrentLocation = false;
          this.isLocationBlocked = true;
        },
      );
    } else {
      this.showMessage('Sorry, geolocation is not supported.', 'error');
      this.isFetchingCurrentLocation = false;
      this.isLocationBlocked = true;
    }
  }

  public getLocationValue() {
    const locationValue = this.parentForm.get('location_group.location')?.value;

    if (!locationValue || !locationValue.coordinates) {
      return '';
    }

    return `${locationValue.coordinates[0]}, ${locationValue.coordinates[1]}`;
  }

  public getCurrentLocationFromForm() {
    const locationValue = this.parentForm.get('location_group.location')?.value;
    if (locationValue && locationValue.coordinates) {
      return {
        lat: locationValue.coordinates[1],
        lng: locationValue.coordinates[0],
      };
    }
    return { lat: 0, lng: 0 };
  }
}
